import React, { useState } from "react";

import { Col, Container, Form, Row, Table } from "react-bootstrap";

import classNames from "classnames";
import { debounce, startCase } from "lodash";
import { ReactSVG } from "react-svg";

import {
  type LatestPublicResalesQueryVariables,
  type PublishedResaleFragment,
  SortDirection,
  useLatestPublicResalesQuery,
} from "@api/graphql";
import { SearchIcon } from "@assets/icons";
import { PageSelect } from "@components/table/Pager";
import { type Header, SimpleTableContents } from "@components/table/TableContents";
import { TableHeaders as CustomTableHeaders } from "@components/table/TableHeaders";

import { PublicFooter } from "./PublicFooter";
import { PublicHeader } from "./PublicHeader";

import styles from "./NoticeOfResale.module.scss";

const dateFormatter = new Intl.DateTimeFormat("en-AU");

const TableHeaders: Header<PublishedResaleFragment>[] = [
  { label: "Artist Name", accessor: "artist_name" },
  { label: "Artwork Title", accessor: "artwork_name" },
  {
    label: "Medium",
    accessor: "artwork_medium",
    formatter: (medium) => startCase(medium.toLocaleLowerCase().replace(/_/g, " ").replace(/ book/, "' book")),
  },
  { label: "Opt Out Deadline", accessor: "opt_out_expiry", formatter: (date) => dateFormatter.format(new Date(date)) },
];

interface SearchBoxProps {
  setFilterValue: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setTotalItems: React.Dispatch<React.SetStateAction<number>>;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
}

const SEARCH_DEBOUNCE_DELAY_MS = 300;

export const SearchBox = (props: SearchBoxProps) => {
  const { setFilterValue, setPage, setTotalItems, setTotalPages } = props;
  const updateFilter = debounce((value) => {
    setFilterValue(value);
    setPage(1);
    setTotalItems(0);
    setTotalPages(0);
  }, SEARCH_DEBOUNCE_DELAY_MS);

  return (
    <Form.Group className={styles.searchInputWrapper}>
      <ReactSVG src={SearchIcon} className={styles.blankBlock} />
      <Form.Control
        type="text"
        placeholder={"Search by artist, artwork..."}
        onChange={(e) => updateFilter(e.target.value)}
        className={classNames(styles.searchInput, "b3")}
      />
      <div className={styles.blankBlock} />
    </Form.Group>
  );
};

const PublicResaleTable = () => {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [filterValue, setFilterValue] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof PublishedResaleFragment>("opt_out_expiry");

  const pageSize = 20;

  const queryVariables: LatestPublicResalesQueryVariables = {
    input: {
      page,
      limit: pageSize,
      sort_by: { field: sortColumn, direction: SortDirection.DESCENDING },
      filter_by: filterValue === "" ? [] : [{ field: "value", like: filterValue }],
    },
  };

  const { data, loading, error } = useLatestPublicResalesQuery({ variables: queryVariables });
  const resales = data?.listPublicResales.results || [];

  if (data) {
    if (totalPages !== data.listPublicResales.total_pages) {
      setTotalPages(data.listPublicResales.total_pages);
    }
    if (totalItems !== data.listPublicResales.total_items) {
      setTotalItems(data.listPublicResales.total_items);
    }
    if (page !== data.listPublicResales.page) {
      setPage(data.listPublicResales.page);
    }
  }

  if (error) {
    return <p>Error: {error.message}</p>;
  }

  return (
    <>
      <SearchBox
        setFilterValue={setFilterValue}
        setPage={setPage}
        setTotalItems={setTotalItems}
        setTotalPages={setTotalPages}
      />
      <PageSelect page={page} totalItems={totalItems} totalPages={totalPages} setPage={setPage} pageSize={pageSize} />{" "}
      {loading ? (
        <div className={`spinner-border ${styles.blankBlock}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : (
        <Table className={styles["resale-table"]} hover>
          <CustomTableHeaders
            headers={TableHeaders}
            sortColumn={sortColumn}
            setSortColumn={setSortColumn}
            customStyles={{ "custom-header-color": "white" }}
          />
          <SimpleTableContents headers={TableHeaders} data={resales} keyAccessor="resale_id" />
        </Table>
      )}
    </>
  );
};

export const NoticeOfResale = () => (
  <div className={classNames(styles["root-container"], "d-flex")}>
    <Container className={classNames(styles.container, "d-flex flex-column")} fluid>
      <Row className={styles["header-row"]}>
        <Col xs={12}>
          <PublicHeader />
        </Col>
      </Row>
      <Row className={styles["content-row"]}>
        <Container>
          <h1 className="display-3 my-4 pt-5 pb-2">Notice of resale</h1>
          <PublicResaleTable />
        </Container>
      </Row>
      <Row className={styles["footer-row"]}>
        <Col className={styles["footer-col"]} xs={12}>
          <PublicFooter />
        </Col>
      </Row>
    </Container>
  </div>
);
